import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Palette, ChevronLeft, ChevronRight, Database } from 'lucide-react';



// SVGs for navigation icons (standardized)
const NavIcons = {
    Home: ({ color = '#1D2739' }: { color?: string }) => (
        <svg width="20" height="20" viewBox="0 0 16 16" fill="none">
            <path d="M6.66667 14V10.6667H9.33333V14M2 6L8 1.33333L14 6V12.6667C14 13.0203 13.8595 13.3594 13.6095 13.6095C13.3594 13.8595 13.0203 14 12.6667 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V6Z" stroke={color} strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    ),
    Templates: ({ color = '#1D2739' }: { color?: string }) => (
        <svg width="20" height="20" viewBox="0 0 16 16" fill="none">
            <path d="M1.33333 4.66667V11.3333C1.33333 12.4379 2.22876 13.3333 3.33333 13.3333H12.6667C13.7712 13.3333 14.6667 12.4379 14.6667 11.3333V4.66667C14.6667 3.5621 13.7712 2.66667 12.6667 2.66667H3.33333C2.22876 2.66667 1.33333 3.5621 1.33333 4.66667Z" stroke={color} strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M1.33333 6.66667H14.6667" stroke={color} strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M4.66667 9.33333H7.33333" stroke={color} strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    ),
    BrandKit: ({ color = '#1D2739' }: { color?: string }) => (
        <Palette width={20} height={20} stroke={color} strokeWidth={1.25} />
    ),
    MyProjects: ({ color = '#1D2739' }: { color?: string }) => (
        <svg width="20" height="20" viewBox="0 0 16 16" fill="none">
            <path d="M2.66667 11.3333V4.66667C2.66667 3.5621 3.5621 2.66667 4.66667 2.66667H8.66667L10.6667 4.66667H13.3333C14.4379 4.66667 15.3333 5.5621 15.3333 6.66667V11.3333C15.3333 12.4379 14.4379 13.3333 13.3333 13.3333H4.66667C3.5621 13.3333 2.66667 12.4379 2.66667 11.3333Z" stroke={color} strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    ),
    Integrations: ({ color = '#1D2739' }: { color?: string }) => (
        <Database width={20} height={20} stroke={color} strokeWidth={1.25} />
    ),
};

const NavItem = ({ icon: Icon, text, onClick, active, isCollapsed }: { icon: React.FC<{ color?: string }>, text: string, onClick?: () => void, active?: boolean, isCollapsed?: boolean }) => (
    <div
        onClick={onClick}
        className={`flex items-center ${isCollapsed ? 'justify-center' : 'gap-[12px] px-[16px]'} h-[44px] relative rounded-[8px] transition-all duration-200 cursor-pointer group`}
        title={isCollapsed ? text : undefined}
    >
        {/* Active State Indicator */}
        <div 
            className="app-selector-indicator" 
            style={{ '--background-width': active ? '100%' : '0%' } as React.CSSProperties} 
        />
        
        <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'gap-[12px]'} relative z-10 w-full`}>
            <Icon color={active ? '#30A84B' : '#475367'} />
            {!isCollapsed && (
                <p className={`font-sans font-medium text-[14px] text-nowrap tracking-[-0.01em] transition-colors duration-400 ${active ? 'app-active-state' : 'text-[#475367]'}`}>
                    {text}
                </p>
            )}
        </div>
    </div>
);

export const Sidebar = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isCollapsed, setIsCollapsed] = React.useState(() => {
        const saved = localStorage.getItem('sidebar-collapsed');
        return saved === 'true';
    });

    React.useEffect(() => {
        localStorage.setItem('sidebar-collapsed', String(isCollapsed));
    }, [isCollapsed]);

    const getActive = (path: string) => location.pathname.startsWith(path);

    return (
        <div 
            className={`h-screen bg-gradient-to-b from-[#E9F7EF] to-[#F1FAF5] border-r border-[#e4e7ec] flex flex-col shrink-0 transition-all duration-300 relative group z-[100] overflow-visible`}
            style={{ width: isCollapsed ? '72px' : '240px' }}
        >
            {/* Toggle Button */}
            <button
                onClick={() => setIsCollapsed(!isCollapsed)}
                className="absolute -right-3 top-8 w-6 h-6 bg-white border border-[#e4e7ec] rounded-full flex items-center justify-center shadow-md z-[101] opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-auto cursor-pointer"
            >
                {isCollapsed ? (
                    <ChevronRight className="w-4 h-4 text-[#475367]" />
                ) : (
                    <ChevronLeft className="w-4 h-4 text-[#475367]" />
                )}
            </button>

            {/* Logo Section */}
            <div 
                className={`h-[72px] flex items-center justify-center pt-2 cursor-pointer hover:opacity-80 transition-all duration-300`}
                onClick={() => navigate('/home')}
            >
                <img 
                    src="/logo.png" 
                    alt="Wejha" 
                    className={`transition-all duration-300 object-contain h-8 w-auto`} 
                />
            </div>

            {/* Navigation Section */}
            <nav className={`flex-1 flex flex-col gap-2 ${isCollapsed ? 'px-2' : 'px-[12px]'}`}>
                <NavItem
                    icon={NavIcons.Home}
                    text="Home"
                    active={location.pathname === '/home' || location.pathname === '/'}
                    onClick={() => navigate('/home')}
                    isCollapsed={isCollapsed}
                />
                <NavItem
                    icon={NavIcons.Templates}
                    text="Templates"
                    active={getActive('/templates')}
                    onClick={() => navigate('/templates')}
                    isCollapsed={isCollapsed}
                />
                <NavItem
                    icon={NavIcons.BrandKit}
                    text="Brand Kit"
                    active={getActive('/brand-kit')}
                    onClick={() => navigate('/brand-kit')}
                    isCollapsed={isCollapsed}
                />
                <NavItem
                    icon={NavIcons.MyProjects}
                    text="My Projects"
                    active={getActive('/my-projects')}
                    onClick={() => navigate('/my-projects')}
                    isCollapsed={isCollapsed}
                />
                <NavItem
                    icon={NavIcons.Integrations}
                    text="Integrations"
                    active={getActive('/integrations')}
                    onClick={() => navigate('/integrations')}
                    isCollapsed={isCollapsed}
                />
            </nav>
        </div>
    );
};
